import { Navigate, Outlet } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Spinner } from "@common/index";
import { ROUTES } from "@routing/routes";
import { currentUserAtom, userFetchErrorAtom } from "@/store/userAtoms";
import { ApprovalStatus, isCustomerWalled } from "@models/user";
import { AccountLoadError } from "./AccountLoadError";

// Route guard for the two access walls, wrapping the gated `/c` routes
// (shopping, browse, search):
//   1. Not approved (E5-S6, FULL WALL) -> /c/pending.
//   2. Approved but the org membership is not Active (#321) -> /c/payment-required.
// The `/c/account` subtree and both wall routes (`/c/pending`,
// `/c/payment-required`) sit OUTSIDE this gate so they stay reachable
// regardless of approval or membership status.
export const ApprovalGate = () => {
    const user = useAtomValue(currentUserAtom);
    const hasFetchError = useAtomValue(userFetchErrorAtom);

    if (!user) {
        // Failed is distinct from loading, exactly as in AdminRoute — the same
        // read guards both trees, and a 500 or a dropped network would
        // otherwise leave every gated `/c` surface a permanent spinner.
        // (#401 review R8.)
        if (hasFetchError) {
            return <AccountLoadError />;
        }
        // Loading window: ProtectedRoute calls getMeUser() on mount, which
        // populates currentUserAtom. Until that resolves we don't know the
        // status, so render a spinner rather than flashing either the app or
        // the pending wall.
        return (
            <div className="flex justify-center py-20">
                <Spinner size="lg" />
            </div>
        );
    }

    // isCustomerWalled is the same predicate a re-read after a 403 asks (#368).
    if (!isCustomerWalled(user)) {
        return <Outlet />;
    }

    // Not approved: the application wall.
    // Approved but the org membership is in a wall state (#321): lapsed,
    // suspended or cancelled orgs, orgs with no active approved location and
    // orgs that never added a card get the membership wall — the page picks
    // the variant. Everything else — Active, NoLocationMembership (#410),
    // absent, null or a value this build does not know — was let through
    // above; see isMembershipWalled.
    return (
        <Navigate
            to={
                user.approvalStatus !== ApprovalStatus.Approved
                    ? ROUTES.PENDING
                    : ROUTES.PAYMENT_REQUIRED
            }
            replace
        />
    );
};
